<template>
  <Navbar :show-back-button="true" @back="navigateToDashboard"/>
  <div class="companies-permissions-container">
    <!-- Header Section -->
    <div class="header-section" style="margin-top: 15px;">
      <div class="header-content">
        <div class="title-badge">
          <i class="fas fa-building-user"></i>
          <span>{{ $t('company.permission.title_badge') }}</span>
        </div>
        <h1 class="main-title">
          <i class="fas fa-shield-alt"></i>
          {{ $t('company.permission.main_title') }}
        </h1>
        <p class="subtitle">{{ $t('company.permission.subtitle') }}</p>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state" style="margin-bottom: 55px;">
      <div class="loading-spinner">
        <div class="spinner-ring"></div>
        <i class="fas fa-building"></i>
      </div>
      <p class="loading-text">{{ $t('company.permission.loading_companies') }}</p>
    </div>

    <!-- Companies Grid -->
    <div v-else class="companies-grid" style="margin-bottom: 55px;">
      <div v-for="(company, index) in companies" 
           :key="company.id" 
           class="company-card"
           :style="{ animationDelay: `${index * 0.1}s` }">
        
        <!-- Company Header -->
        <div class="company-header">
          <div class="company-avatar">
            <i class="fas fa-building"></i>
          </div>
          <div class="company-info">
            <h2 class="company-name">{{ company.name }}</h2>
            <div class="company-meta">
              <span class="company-id">
                <i class="fas fa-fingerprint"></i>
                {{ $t('company.permission.company_id') }}: {{ company.id }}
              </span>
              <!-- <span class="company-status" :class="getCompanyStatus(company)">
                <i class="fas fa-circle"></i>
                {{ $t('company.permission.active') }}
              </span> -->
            </div>
          </div>
          <div class="company-expand" @click="toggleCompany(company.id)">
            <i class="fas" :class="expandedCompanies[company.id] ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </div>
        </div>

        <!-- Permissions Section (Collapsible) -->
        <transition name="expand">
          <div v-show="expandedCompanies[company.id]" class="permissions-section">
            <div class="permissions-wrapper">
              <AdminCompanyPermissions :companyId="company.id" />
            </div>
          </div>
        </transition>
      </div>

      <!-- Empty State -->
      <div v-if="companies.length === 0" class="empty-state">
        <i class="fas fa-building-circle-exclamation"></i>
        <h3>{{ $t('company.permission.no_companies_found') }}</h3>
        <p>{{ $t('company.permission.no_companies_message') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { adminCompanyService } from '@/services/api'
import AdminCompanyPermissions from './AdminCompanyPermissions.vue'
import Navbar from '@/components/Navbar.vue'
import { useRouter } from 'vue-router'

const companies = ref([])
const loading = ref(true)
const expandedCompanies = ref({})
const router = useRouter()

onMounted(async () => {
  try {
    const res = await adminCompanyService.getAllCompanies()
    companies.value = res.data || res
    companies.value.forEach(company => {
      expandedCompanies.value[company.id] = false
    })
  } catch (err) {
    console.error("Failed to load companies", err)
  } finally {
    loading.value = false
  }
})

const toggleCompany = (companyId) => {
  expandedCompanies.value[companyId] = !expandedCompanies.value[companyId]
}

const navigateToDashboard = () => {
  router.push('/admin/dashboard')
}

// const getCompanyStatus = () => {
//   return 'active'
// }
</script>

<style scoped>
/* Modern CSS Reset & Base */
.companies-permissions-container {
  min-height: 100vh;
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  padding: 2rem;
}

/* Header Section */
.header-section {
  margin-bottom: 3rem;
  animation: slideDown 0.5s ease-out;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
}

.title-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  margin-bottom: 1.5rem;
  letter-spacing: 0.5px;
}

.main-title {
  font-size: 3rem;
  font-weight: 800;
  color: white;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px var(--shadow-1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.main-title i {
  font-size: 2.5rem;
  filter: drop-shadow(2px 2px 4px var(--shadow-1));
}

.subtitle {
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.loading-spinner {
  position: relative;
  width: 80px;
  height: 80px;
  margin-bottom: 1.5rem;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-spinner i {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 2rem;
  color: white;
  animation: pulse 1.5s ease-in-out infinite;
}

.loading-text {
  color: white;
  font-size: 1.125rem;
  font-weight: 500;
}

/* Companies Grid */
.companies-grid {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Company Card */
.company-card {
  background: var(--card-bg);
  border-radius: 1.5rem;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px var(--shadow-2), 0 10px 10px -5px var(--shadow-1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: fadeInUp 0.5s ease-out backwards;
  border: 1px solid var(--card-border);
}

.company-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 25px 30px -12px var(--shadow-2);
}

/* Company Header */
.company-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 2rem;
  /* background: linear-gradient(135deg, var(--bg) 0%, var(--card-bg) 100%); */
  cursor: pointer;
  transition: background 0.2s ease;
  border-bottom: 2px solid var(--card-border);
}

.company-header:hover {
  background: linear-gradient(135deg, var(--ring) 0%, var(--bg) 100%);
}

.company-avatar {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  color: white;
  transition: transform 0.2s ease;
}

.company-card:hover .company-avatar {
  transform: scale(1.05);
}

.company-info {
  flex: 1;
}

.company-name {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--heading);
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.5px;
}

.company-meta {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.company-id {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  background: var(--ring);
  border-radius: 20px;
  font-size: 0.813rem;
  font-weight: 500;
  color: var(--subtext);
}

.company-status {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.813rem;
  font-weight: 600;
}

.company-status.active {
  background: rgba(16, 185, 129, 0.15);
  color: var(--green);
}

.company-status.inactive {
  background: rgba(249, 115, 22, 0.15);
  color: var(--orange-1);
}

.company-status i {
  font-size: 0.5rem;
}

.company-expand {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--ring);
  border-radius: 50%;
  transition: all 0.2s ease;
  color: var(--subtext);
}

.company-expand:hover {
  background: var(--card-border);
  transform: scale(1.1);
  color: var(--primary);
}

/* Permissions Section */
.permissions-section {
  background: var(--card-bg);
  overflow: hidden;
}

.permissions-wrapper {
  padding: 2rem;
  background: linear-gradient(135deg, var(--bg) 0%, var(--card-bg) 100%);
  border-top: 1px solid var(--card-border);
}

/* Collapsible Animation */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease-out;
  max-height: 2000px;
  opacity: 1;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  background: var(--card-bg);
  border-radius: 1.5rem;
  color: var(--subtext);
  border: 1px solid var(--card-border);
}

.empty-state i {
  font-size: 4rem;
  margin-bottom: 1rem;
  color: var(--muted);
}

.empty-state h3 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--heading);
}

.empty-state p {
  font-size: 1rem;
  color: var(--subtext);
}

/* Animations */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .companies-permissions-container {
    padding: 0.3rem;
  }

  .main-title {
    font-size: 1.75rem;
  }

  .main-title i {
    font-size: 1.5rem;
  }

  .subtitle {
    font-size: 0.875rem;
  }

  .company-header {
    margin-top: 15px;
    padding: 0rem 1rem;
  }

  .company-avatar {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }

  .company-name {
    font-size: 1.125rem;
  }

  .permissions-wrapper {
    padding: 0.5rem;
  }

  .company-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: var(--bg);
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: var(--muted-2);
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--muted);
}
</style>

<!-- Add Font Awesome if not already included -->
<style>
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');
</style>